import React, { useState } from 'react';
import api from '../../lib/api';

/**
 * AddCustomFieldInline — טופס קטן ליצירת שדה מותאם חדש לארגון, משובץ גם
 * בטופס "איש קשר חדש" וגם בטאב העריכה של כרטיס איש קשר. שומר את ההגדרה
 * ברמת הארגון (PUT /permissions/contact-custom-fields) ומחזיר את השדה
 * החדש ל-caller כדי שיתווסף מיד לטופס הפתוח.
 */

export interface NewCustomFieldDef {
  id: string;
  label: string;
  type: 'text' | 'number' | 'date' | 'select' | 'checkbox';
  options?: string[];
}

const TYPE_OPTIONS: Array<{ value: NewCustomFieldDef['type']; label: string }> = [
  { value: 'text', label: 'טקסט' },
  { value: 'number', label: 'מספר' },
  { value: 'date', label: 'תאריך' },
  { value: 'select', label: 'רשימה עם אפשרויות' },
  { value: 'checkbox', label: 'כן / לא (V)' },
];

const FIELD_CLASS = 'w-full px-3 py-2 border border-gray-300 rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm';

const AddCustomFieldInline: React.FC<{
  /** current org field defs — the PUT persists the whole array */
  existingFields: any[];
  onCreated: (field: NewCustomFieldDef, allFields: any[]) => void;
  onCancel: () => void;
}> = ({ existingFields, onCreated, onCancel }) => {
  const [name, setName] = useState('');
  const [type, setType] = useState<NewCustomFieldDef['type']>('text');
  const [options, setOptions] = useState('');
  const [saving, setSaving] = useState(false);

  const create = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      const field: NewCustomFieldDef = {
        id: `field_${Date.now()}`,
        label: name.trim(),
        type,
        options: type === 'select'
          ? options.split('\n').map((s) => s.trim()).filter(Boolean)
          : undefined,
      };
      const all = [...existingFields, field];
      await api.put('/permissions/contact-custom-fields', { fields: all });
      onCreated(field, all);
      setName(''); setType('text'); setOptions('');
    } catch {
      alert('שגיאה ביצירת השדה המותאם');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4 space-y-3">
      <div>
        <label className="block text-xs text-gray-500 mb-1">שם השדה *</label>
        <input
          type="text"
          autoFocus
          placeholder="לדוגמה: דרגה / קטגוריה / תאריך הצטרפות"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className={FIELD_CLASS}
        />
      </div>
      <div>
        <label className="block text-xs text-gray-500 mb-1">סוג השדה</label>
        <select value={type} onChange={(e) => setType(e.target.value as any)} className={FIELD_CLASS}>
          {TYPE_OPTIONS.map((t) => (
            <option key={t.value} value={t.value}>{t.label}</option>
          ))}
        </select>
      </div>
      {type === 'select' && (
        <div>
          <label className="block text-xs text-gray-500 mb-1">אפשרויות (שורה אחת לכל אפשרות)</label>
          <textarea
            placeholder={'אפשרות 1\nאפשרות 2\nאפשרות 3'}
            value={options}
            onChange={(e) => setOptions(e.target.value)}
            rows={3}
            className={FIELD_CLASS + ' resize-none'}
          />
        </div>
      )}
      <div className="flex gap-2 justify-end">
        <button
          type="button"
          onClick={onCancel}
          className="px-3 py-1.5 text-xs bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600"
        >
          ביטול
        </button>
        <button
          type="button"
          onClick={create}
          disabled={!name.trim() || saving}
          className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
        >
          {saving ? 'יוצר…' : 'צור שדה'}
        </button>
      </div>
    </div>
  );
};

export default AddCustomFieldInline;
